import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../api/Client";
import { SecureStorage } from "../../storage/SecureStorage";
import styles from "./ProfilePage.module.css";

type MeResponse = {
  data?: {
    name?: string;
    email?: string;
    phone?: string;
    date_of_birth?: string | null;
    gender?: string | null;
    postal_code?: string | number | null;
    comentario?: string | null;
    customer_company?: string | null;
    dni?: string | null;
    address?: string | null;
  };
};

type StoredUser = {
  fullName?: string;
  email?: string;
  phone?: string;
  sex?: string;
  dateOfBirth?: string;
  postcode?: string | number | null;
  postAddress?: string | null;
  dni?: string | null;
};

type ProfileFieldProps = {
  label: string;
  value: string;
};

export default function ProfilePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<MeResponse["data"] | null>(null);
  const [storedUser, setStoredUser] = useState<StoredUser | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      setLoading(true);
      setError(null);
      try {
        const stored = await SecureStorage.getUser();
        if (mounted) {
          setStoredUser(stored as StoredUser);
        }
        const response = await api.get<MeResponse>("/api/customers/me");
        if (!mounted) {
          return;
        }
        setProfile(response.data?.data ?? null);
      } catch (requestError: any) {
        if (!mounted) {
          return;
        }
        setError(requestError?.response?.data?.error || "No se pudo cargar tu perfil.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void loadProfile();
    return () => {
      mounted = false;
    };
  }, []);

  const fields = useMemo<ProfileFieldProps[]>(() => {
    if (!profile) {
      return [];
    }

    return [
      { label: "Nombre completo", value: sanitize(profile.name || storedUser?.fullName) },
      { label: "Correo electronico", value: sanitize(profile.email || storedUser?.email) },
      { label: "Telefono", value: sanitize(profile.phone || storedUser?.phone) },
      { label: "Sexo", value: formatGender(profile.gender || storedUser?.sex) },
      { label: "Fecha de nacimiento", value: formatDate(profile.date_of_birth || storedUser?.dateOfBirth) },
      { label: "DNI", value: sanitize(profile.dni || storedUser?.dni) },
      { label: "Direccion postal", value: sanitize(profile.address || storedUser?.postAddress) },
      { label: "Codigo postal", value: sanitize(profile.postal_code || storedUser?.postcode) },
      { label: "Empresa", value: sanitize(profile.customer_company) },
      { label: "Comentario", value: sanitize(profile.comentario) },
    ];
  }, [profile, storedUser]);

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <div className={styles.header}>
          <div>
            <h1>Mi perfil</h1>
            <p>Revisa y actualiza tus datos personales.</p>
          </div>
          <button type="button" className={styles.editButton} onClick={() => navigate("/profile/edit")}>
            Editar
          </button>
        </div>

        {loading && <p className={styles.stateText}>Cargando perfil...</p>}
        {error && !loading && <p className={styles.errorText}>{error}</p>}

        {!loading && !error && (
          <div className={styles.grid}>
            {fields.map((field) => (
              <ProfileField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function ProfileField({ label, value }: ProfileFieldProps) {
  return (
    <article className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </article>
  );
}

function sanitize(value: unknown): string {
  const normalized = String(value ?? "").trim();
  return normalized || "--";
}

function formatDate(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) {
    return "--";
  }
  const ymd = raw.slice(0, 10);
  const parts = ymd.split("-");
  if (parts.length !== 3) {
    return ymd;
  }
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function formatGender(value: string | null | undefined): string {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return "--";
  }
  if (normalized === "male" || normalized === "masculino") {
    return "Masculino";
  }
  if (normalized === "female" || normalized === "femenino") {
    return "Femenino";
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
